import { useIntersectionObserver } from '@vueuse/core'
import { type Ref, ref } from 'vue'
import type { Section } from '@/types'

export function useScrollNavigation (sections: Section[]) {
  // State
  const formContainer = ref<HTMLElement | null>(null)
  const activeSection = ref<string>(sections[0]?.id ?? '')
  const isScrolling = ref(false)
  const sectionRefs: Record<string, Ref<HTMLElement | null>> = {}

  // Initialize refs for each section
  sections.forEach((section) => {
    sectionRefs[section.id] = ref(null)
  })

  // Function to bind refs to FormSection elements
  const setSectionRef = (id: string) => (el: HTMLElement | null) => {
    sectionRefs[id].value = el
  }

  // Scroll to a section
  const scrollToSection = (id: string) => {
    if (isScrolling.value || !sectionRefs[id]?.value || !formContainer.value) {
      console.warn(`Cannot scroll to section ${id}: Invalid ref or container`)
      return
    }

    isScrolling.value = true
    activeSection.value = id

    const element = sectionRefs[id].value!
    const container = formContainer.value!
    const scrollTop = element.offsetTop - 160

    container.scrollTo({
      top: scrollTop,
      behavior: 'smooth',
    })

    setTimeout(() => {
      isScrolling.value = false
    }, 600)
  }

  // IntersectionObserver setup
  sections.forEach((section) => {
    useIntersectionObserver(
      sectionRefs[section.id],
      (entries) => {
        entries.forEach((entry) => {
          if (isScrolling.value || !entry.isIntersecting) return
          if (entry.intersectionRatio >= 0.3) {
            activeSection.value = section.id
          }
        })
      },
      {
        root: formContainer,
        threshold: [0.3, 0.5, 0.7],
        rootMargin: '-10% 0px -10% 0px',
      },
    )
  })

  return {
    formContainer,
    activeSection,
    sectionRefs,
    setSectionRef,
    scrollToSection,
  }
}
